import React, { useState } from 'react'
import {
  Box,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Stack,
} from '@mui/material'
import { MoreVert as MoreVertIcon } from '@mui/icons-material'
import { TOUCH_TARGET_MIN_PX } from './layoutConstants'

export type PageHeaderOverflowItem = {
  id: string
  label: string
  onClick: () => void
  icon?: React.ReactNode
  disabled?: boolean
}

export type PageHeaderPrimaryAction = {
  label: string
  onClick: () => void
  icon?: React.ReactNode
  'aria-label'?: string
}

type PageHeaderProps = {
  title: string
  primaryAction?: PageHeaderPrimaryAction
  overflowItems?: PageHeaderOverflowItem[]
  /** Accessible label for the overflow menu trigger. */
  overflowMenuLabel?: string
}

export function PageHeader({
  title,
  primaryAction,
  overflowItems = [],
  overflowMenuLabel = 'More actions',
}: PageHeaderProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const menuOpen = Boolean(anchorEl)

  const handleMenuClose = () => setAnchorEl(null)

  const handleOverflowClick = (item: PageHeaderOverflowItem) => {
    handleMenuClose()
    item.onClick()
  }

  return (
    <Box
      component="header"
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'stretch', sm: 'center' },
        justifyContent: 'space-between',
        gap: 1,
        mb: 2,
      }}
    >
      <Typography
        variant="h5"
        component="h1"
        noWrap
        sx={{ fontWeight: 600, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}
      >
        {title}
      </Typography>

      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent={{ xs: 'flex-end', sm: 'flex-start' }}
        sx={{ flexShrink: 0, width: { xs: '100%', sm: 'auto' } }}
      >
        {primaryAction && (
          <Button
            variant="contained"
            startIcon={primaryAction.icon}
            onClick={primaryAction.onClick}
            aria-label={primaryAction['aria-label'] ?? primaryAction.label}
            sx={{ minHeight: TOUCH_TARGET_MIN_PX, flex: { xs: 1, sm: '0 1 auto' } }}
          >
            {primaryAction.label}
          </Button>
        )}

        {overflowItems.length > 0 && (
          <>
            <IconButton
              aria-label={overflowMenuLabel}
              aria-controls={menuOpen ? 'page-header-overflow-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={menuOpen ? 'true' : undefined}
              onClick={(e) => setAnchorEl(e.currentTarget)}
              sx={{ minWidth: TOUCH_TARGET_MIN_PX, minHeight: TOUCH_TARGET_MIN_PX }}
            >
              <MoreVertIcon />
            </IconButton>
            <Menu
              id="page-header-overflow-menu"
              anchorEl={anchorEl}
              open={menuOpen}
              onClose={handleMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              {overflowItems.map((item) => (
                <MenuItem
                  key={item.id}
                  onClick={() => handleOverflowClick(item)}
                  disabled={item.disabled}
                  sx={{ minHeight: TOUCH_TARGET_MIN_PX }}
                >
                  {item.icon && <ListItemIcon>{item.icon}</ListItemIcon>}
                  <ListItemText>{item.label}</ListItemText>
                </MenuItem>
              ))}
            </Menu>
          </>
        )}
      </Stack>
    </Box>
  )
}

export default PageHeader
